import * as React from 'react';

import { Button, DataGridColumn, Menu, MenuItem } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue } from 'jotai';
import {
  LucideEdit,
  LucideExternalLink,
  LucideMoreHorizontal,
  LucideTrash2,
} from 'lucide-react';

import { $facilitiesById } from '../../_state/facilities.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryTagRecord } from '../../types/inventoryTag';

import { InventoryTagStatusBadge } from './InventoryTagStatusBadge';
import { TagTypeBadge } from './TagTypeBadge';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type UseInventoryTagsColumnsArgs = {
  facilityMap?: Map<string, string>;
  onRowClick?: (cell: { row: InventoryTagRecord }) => void;
  onEdit?: (tag: InventoryTagRecord) => void;
  onDelete?: (tag: InventoryTagRecord) => void;
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatQuantity = (value: number): string => {
  if (value === 0) return '0';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const renderTagNumberCell = ({ row }: { row: InventoryTagRecord }) => (
  <div className="flex flex-col">
    <span className="font-medium">{row.tagNumber}</span>
    <span className="text-xs text-neutral/60">{row.itemName}</span>
  </div>
);

const renderTagTypeCell = ({ row }: { row: InventoryTagRecord }) => (
  <TagTypeBadge tagType={row.tagType} />
);

const renderStatusCell = ({ row }: { row: InventoryTagRecord }) => (
  <InventoryTagStatusBadge status={row.status} />
);

const renderNetQuantityCell = ({ row }: { row: InventoryTagRecord }) => (
  <span className="text-sm tabular-nums">
    {formatQuantity(row.netQuantity)}
  </span>
);

const renderGeneratedAtCell = ({ row }: { row: InventoryTagRecord }) => (
  <span className="text-sm">{formatDate(row.generatedAt)}</span>
);

export const useInventoryTagsColumns = ({
  facilityMap,
  onRowClick,
  onEdit,
  onDelete,
}: UseInventoryTagsColumnsArgs = {}): DataGridColumn<InventoryTagRecord>[] => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();
  const facilitiesById = useAtomValue($facilitiesById);

  const renderFacilityCell = React.useCallback(
    ({ row }: { row: InventoryTagRecord }) => {
      const facilityName =
        facilitiesById?.[row.facilityId]?.name ||
        facilityMap?.get(row.facilityId) ||
        row.facilityId;
      return <span className="text-sm">{facilityName}</span>;
    },
    [facilitiesById, facilityMap],
  );

  const handleLotClick = React.useCallback(
    (e: React.MouseEvent, lotId: string) => {
      e.stopPropagation();
      setLocation(`/inventory/lots/${lotId}`);
    },
    [setLocation],
  );

  const renderLotCell = React.useCallback(
    ({ row }: { row: InventoryTagRecord }) => {
      if (!row.lotId || !row.lotNumber) {
        return <span className="text-neutral/40">—</span>;
      }
      return (
        <button
          type="button"
          onClick={(e) => row.lotId && handleLotClick(e, row.lotId)}
          className={cn(
            'group flex items-center gap-1 text-sm text-primary hover:underline',
          )}
        >
          <span>{row.lotNumber}</span>
          <LucideExternalLink className="size-3 opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      );
    },
    [handleLotClick],
  );

  return React.useMemo(() => {
    const columns: DataGridColumn<InventoryTagRecord>[] = [
      {
        key: 'tagNumber',
        header: t.tagsListView.table.columns.tagNumber,
        onClick: onRowClick,
        cell: renderTagNumberCell,
        width: 200,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.tagsListView.table.columns.facility,
        onClick: onRowClick,
        cell: renderFacilityCell,
        width: 180,
      },
      {
        key: 'tagType',
        header: t.tagsListView.table.columns.tagType,
        onClick: onRowClick,
        cell: renderTagTypeCell,
        width: 140,
      },
      {
        key: 'status',
        header: t.tagsListView.table.columns.status,
        onClick: onRowClick,
        cell: renderStatusCell,
        width: 130,
      },
      {
        key: 'netQuantity',
        header: t.tagsListView.table.columns.netQuantity,
        onClick: onRowClick,
        cell: renderNetQuantityCell,
        width: 100,
      },
      {
        key: 'lot',
        header: t.tagsListView.table.columns.lot,
        cell: renderLotCell,
        width: 120,
      },
      {
        key: 'generatedAt',
        header: t.tagsListView.table.columns.generatedAt,
        onClick: onRowClick,
        cell: renderGeneratedAtCell,
        width: 130,
        sortable: true,
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const tag = row as InventoryTagRecord;

          const menuItems: MenuItem[] = [
            {
              label: (
                <div className="flex items-center gap-2">
                  <LucideEdit className="size-4" />
                  {t.tagsListView.actions.edit}
                </div>
              ),
              onAction: () => onEdit?.(tag),
            },
            'separator',
            {
              label: (
                <div className="flex items-center gap-2 text-failure">
                  <LucideTrash2 className="size-4" />
                  {t.tagsListView.actions.delete}
                </div>
              ),
              onAction: () => onDelete?.(tag),
            },
          ];

          return (
            <div className="flex justify-center">
              <Menu
                trigger={
                  <Button
                    size="icon"
                    icon={<LucideMoreHorizontal />}
                    variant="outline"
                  />
                }
                items={menuItems}
              />
            </div>
          );
        },
      },
    ];

    return columns;
  }, [t, onRowClick, onEdit, onDelete, renderFacilityCell, renderLotCell]);
};
